import { CheckCircleOutlined, StopOutlined } from "@ant-design/icons";
import {
  Button,
  Input,
  Layout,
  Select,
  Table,
  TablePaginationConfig,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IPlayerCardService } from "@shared/interfaces/player-card-service.interface";
import { Tiers } from "@shared/resources/tiers";
import { PlayerCardDto } from "@shared/types/player-card/player-card.dto";
import { useMutation, UseMutationResult, useQuery } from "react-query";
import ChangePlayerModal from "./ChangePlayerModal/ChangePlayerModal";
import ImportJerseyNumbersCsvModal from "./ImportJerseyNumbersCsvModal/ImportJerseyNumbersCsvModal";
import SetTeamJerseyColorsModal from "./SetTeamJerseyColorsModal/SetTeamJerseyColorsModal";
import styles from "./Players.module.scss";

type TierType = "ICON" | "PLATINUM" | "GOLD" | "SILVER" | "BRONZE";

const ALL = "All";

const getTierByType = (type: TierType) => {
  switch (type.toUpperCase()) {
    case "ICON": {
      return <Tag color="#8b0000">{type}</Tag>;
    }

    case "PLATINUM": {
      return <Tag color="#9400d3">{type}</Tag>;
    }

    case "GOLD": {
      return <Tag color="#aa6c39">{type}</Tag>;
    }

    case "SILVER": {
      return <Tag color="#c0c0c0">{type}</Tag>;
    }

    case "BRONZE": {
      return <Tag color="#5c3a0a">{type}</Tag>;
    }

    default:
      return null;
  }
};

const getStatusByType = (status: boolean) =>
  status ? (
    <StopOutlined style={{ color: "red", fontSize: 18 }} />
  ) : (
    <CheckCircleOutlined style={{ color: "green", fontSize: 18 }} />
  );

const defaultPlayer = {
  playerName: "",
  team: "",
  division: "",
  position: "",
  tier: "",
  draftYear: dayjs().year(),
  isDisabled: false,
  playerImageUrl: "https://",
  teamImageUrl: "https://",
  jerseyNumber: null,
  jerseyNumberColor: null,
  meta: {},
};

const columns = [
  {
    key: "id",
    dataIndex: "id",
    title: "ID",
  },
  {
    key: "playerName",
    dataIndex: "playerName",
    title: "Name",
  },
  {
    key: "team",
    dataIndex: "team",
    title: "Team",
  },
  {
    key: "division",
    dataIndex: "division",
    title: "Division",
  },
  {
    key: "position",
    dataIndex: "position",
    title: "Position",
  },
  {
    key: "tier",
    dataIndex: "tier",
    title: "Tier",
    render: getTierByType,
  },
  {
    key: "jerseyNumber",
    dataIndex: "jerseyNumber",
    title: "Jersey #",
    render: (value: number | null) => value ?? "-",
  },
  {
    key: "jerseyNumberColor",
    dataIndex: "jerseyNumberColor",
    title: "Jersey # color",
    render: (value: string | null) =>
      value ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: 2,
              backgroundColor: value,
              border: "1px solid rgba(0,0,0,0.15)",
              flexShrink: 0,
            }}
          />
          {value}
        </span>
      ) : (
        "-"
      ),
  },
  {
    key: "draftYear",
    dataIndex: "draftYear",
    title: "Draft Year",
  },
  {
    key: "isDisabled",
    dataIndex: "isDisabled",
    title: "Disabled",
    render: getStatusByType,
  },
];

const ALL_OBJECT = { label: "All", value: ALL };

const TIERS = [{ ...ALL_OBJECT, label: "Filter by tier" }, ...Tiers];

const DISABLED_FILTER = [
  { ...ALL_OBJECT, label: "Filter by status" },
  { label: "Enabled", value: 0 },
  { label: "Disabled", value: 1 },
];

const JERSEY_NUMBER_FILTER = [
  { ...ALL_OBJECT, label: "Jersey number" },
  { label: "Missing jersey #", value: "0" },
  { label: "Has jersey #", value: "1" },
];

const JERSEY_NUMBER_COLOR_FILTER = [
  { ...ALL_OBJECT, label: "Jersey # color" },
  { label: "Missing jersey # color", value: "0" },
  { label: "Has jersey # color", value: "1" },
];

const Players = () => {
  const [searchParams] = useSearchParams();
  const isDevMode = searchParams.has("dev");

  const [searchText, setSearchText] = useState("");
  const [tier, setTier] = useState(ALL);
  const [disabled, setDisabled] = useState(ALL);
  const [jerseyNumberSet, setJerseyNumberSet] = useState(ALL);
  const [jerseyNumberColorSet, setJerseyNumberColorSet] = useState(ALL);
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  const [page, setPage] = useState(1);
  const [currentPageSize, setPageSize] = useState(20);

  const [containerHeight, setContainerHeight] = useState(0);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerCardDto | null>(
    null,
  );
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [teamColorsOpen, setTeamColorsOpen] = useState(false);

  const playerCardsService = appInject<IPlayerCardService>(
    DI_TOKENS.playerCardService,
  );

  const downloadPlayerCardsQuery = useQuery(
    [
      "download-player-cards-list",
      page,
      currentPageSize,
      debouncedSearchText,
      tier,
      disabled,
      jerseyNumberSet,
      jerseyNumberColorSet,
    ],
    () =>
      playerCardsService.getPlayerCardsList({
        page,
        pageSize: currentPageSize,
        query: debouncedSearchText,
        tier: tier === ALL ? undefined : tier ? [tier] : undefined,
        disabled: disabled === ALL ? undefined : (disabled ?? undefined),
        jerseyNumberSet:
          jerseyNumberSet === ALL ? undefined : String(jerseyNumberSet),
        jerseyNumberColorSet:
          jerseyNumberColorSet === ALL ? undefined : String(jerseyNumberColorSet),
      }),
    {
      refetchOnMount: true,
    },
  );

  const downloadTeamsQuery = useQuery(
    ["download-teams"],
    () => playerCardsService.getTeams(),
    {
      refetchOnMount: true,
    },
  );

  const createPlayerMutation: UseMutationResult<
    PlayerCardDto,
    unknown,
    PlayerCardDto,
    unknown
  > = useMutation(
    (data: PlayerCardDto): Promise<PlayerCardDto> =>
      playerCardsService.create(data),
    {
      onSuccess: () => {
        setSelectedPlayer(null);
        setIsOpenModal(false);
        downloadPlayerCardsQuery.refetch();
      },
    },
  );

  const updatePlayerMutation: UseMutationResult<
    PlayerCardDto,
    unknown,
    PlayerCardDto,
    unknown
  > = useMutation(
    (data: PlayerCardDto): Promise<PlayerCardDto> =>
      playerCardsService.update(data),
    {
      onSuccess: () => {
        setSelectedPlayer(null);
        setIsOpenModal(false);
        downloadPlayerCardsQuery.refetch();
      },
    },
  );

  const syncJerseyNumbersMutation = useMutation(
    () => playerCardsService.syncJerseyNumbersFromApi(),
    {
      onSuccess: (response) => {
        const result = response?.data;
        void message.success(
          result
            ? `Synced jersey numbers. Updated ${result.totalUpdated} of ${result.totalPlayerCards} cards.`
            : "Synced jersey numbers.",
        );
        downloadPlayerCardsQuery.refetch();
      },
      onError: () => {
        void message.error("Failed to sync jersey numbers");
      },
    },
  );

  const handleCloseDialog = () => {
    setIsOpenModal(false);
    setSelectedPlayer(null);
  };

  const handleSaveDialog = (data: PlayerCardDto) => {
    if (selectedPlayer?.id) {
      updatePlayerMutation.mutate({ ...data, id: selectedPlayer.id });
    } else if (selectedPlayer) {
      createPlayerMutation.mutate(data);
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);

  const onOpenPlayerCard = (player: PlayerCardDto) => {
    setSelectedPlayer(player);
    setIsOpenModal(true);
  };

  const onTableChange = ({ current, pageSize }: TablePaginationConfig) => {
    setPage(current || 1);
    if (pageSize !== currentPageSize) {
      setPageSize(pageSize || 20);
      setPage(1);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.offsetHeight - 140);
      }
    }, 100);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      setDebouncedSearchText(searchText);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [jerseyNumberSet, jerseyNumberColorSet, disabled, tier]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const resetFilters = () => {
    setSearchText("");
    setDisabled(ALL);
    setTier(ALL);
    setJerseyNumberSet(ALL);
    setJerseyNumberColorSet(ALL);
  };

  const closeCsvImport = () => {
    setCsvImportOpen(false);
  };

  return (
    <Layout className={styles.tableLayout}>
      {isOpenModal ? (
        <ChangePlayerModal
          teams={downloadTeamsQuery.data?.data.teams || []}
          open={isOpenModal}
          player={selectedPlayer}
          onClose={handleCloseDialog}
          onSave={handleSaveDialog}
          isLoading={
            createPlayerMutation.isLoading || updatePlayerMutation.isLoading
          }
          isError={createPlayerMutation.isError || updatePlayerMutation.isError}
        />
      ) : null}

      {csvImportOpen ? (
        <ImportJerseyNumbersCsvModal
          open={csvImportOpen}
          onClose={closeCsvImport}
          onSuccess={() => {
            closeCsvImport();
            downloadPlayerCardsQuery.refetch();
          }}
        />
      ) : null}

      {teamColorsOpen ? (
        <SetTeamJerseyColorsModal
          open={teamColorsOpen}
          onClose={() => setTeamColorsOpen(false)}
          onSuccess={() => {
            setTeamColorsOpen(false);
            downloadPlayerCardsQuery.refetch();
            downloadTeamsQuery.refetch();
          }}
        />
      ) : null}

      <div className={styles.pageHeader}>
        <Typography.Title style={{ margin: 0 }} level={5}>
          Players ({downloadPlayerCardsQuery.data?.total})
        </Typography.Title>

        <Button
          type="primary"
          disabled={downloadTeamsQuery.isLoading}
          onClick={() => {
            setSelectedPlayer(defaultPlayer);
            setIsOpenModal(true);
          }}
        >
          Add Player
        </Button>

        {isDevMode ? (
          <>
            <Button
              onClick={() => syncJerseyNumbersMutation.mutate()}
              loading={syncJerseyNumbersMutation.isLoading}
            >
              Sync jersey numbers
            </Button>
            <Button onClick={() => setCsvImportOpen(true)}>
              Import jersey CSV
            </Button>
            <Button
              disabled={downloadTeamsQuery.isLoading}
              onClick={() => setTeamColorsOpen(true)}
            >
              Set team jersey colors
            </Button>
          </>
        ) : null}
      </div>

      <div className={styles.filterToolbar}>
        <Input
          className={styles.searchInput}
          onChange={handleSearch}
          value={searchText}
          name="search"
          allowClear
          placeholder="Search by player, team, division, position…"
        />

        <Select
          className={styles.filterSelect}
          options={DISABLED_FILTER}
          value={disabled}
          onChange={setDisabled}
        />

        <Select
          className={styles.filterSelect}
          options={TIERS}
          value={tier}
          onChange={setTier}
        />

        <Select
          className={styles.filterSelect}
          options={JERSEY_NUMBER_FILTER}
          value={jerseyNumberSet}
          onChange={setJerseyNumberSet}
        />

        <Select
          className={styles.filterSelect}
          options={JERSEY_NUMBER_COLOR_FILTER}
          value={jerseyNumberColorSet}
          onChange={setJerseyNumberColorSet}
        />

        <Button
          className={styles.resetButton}
          type="primary"
          onClick={resetFilters}
        >
          Reset
        </Button>
      </div>

      <Layout.Content
        ref={containerRef}
        className={styles.content}
        style={{ backgroundColor: "white" }}
      >
        <Table
          rowKey="id"
          dataSource={downloadPlayerCardsQuery.data?.data || []}
          onRow={(player: PlayerCardDto) => ({
            onClick: () => onOpenPlayerCard(player),
          })}
          onChange={onTableChange}
          columns={columns}
          loading={downloadPlayerCardsQuery.isFetching}
          pagination={{
            current: page,
            total: downloadPlayerCardsQuery.data?.total || 0,
            pageSize: currentPageSize,
            showSizeChanger: true,
          }}
          size="small"
          scroll={{ y: containerHeight }}
        />
      </Layout.Content>
    </Layout>
  );
};

export default Players;
